/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { Box, Stack, Typography } from "@mui/material";
import { FC, useCallback, useState } from "react";
import { JobInputCard, JobInputItem } from "./JobInputCard";
import { PanelContentsContainer } from "styles/TaskRegister";
import { BorderedIconButton } from "components/Common/BorderedIconButton";
import AddIcon from "components/Common/AddIcon";
import { createInitialJoInputItem } from "./utils";
import { useTranslation } from "react-i18next";
import { TaskControlMode } from "types";

interface MultipleChargeSelectJobPanelProps {
  jobs?: JobInputItem[];
  mode: TaskControlMode;
  onAdd?: (jobs: JobInputItem[], addedJobId: number, active: boolean) => void;
  onEdit?: (jobs: JobInputItem[], editedJobId: number, active: boolean) => void;
  onDelete?: (
    jobs: JobInputItem[],
    deletedJobId: number,
    active: boolean
  ) => void;
  onActiveJobChanged?: (job: JobInputItem) => void;
}
export const MultipleChargeSelectJobPanel: FC<
  MultipleChargeSelectJobPanelProps
> = ({ jobs, mode, onAdd, onEdit, onDelete, onActiveJobChanged }) => {
  const { t } = useTranslation();
  const [registeredJobs, setRegisteredJobs] = useState<JobInputItem[]>(
    jobs ?? []
  );
  const [activeJobId, setActiveJobId] = useState<number>();

  const handleJobCardDelete = useCallback(
    (jobId: number) => {
      const tempJobs = registeredJobs.filter((job) => job.jobId !== jobId);
      setRegisteredJobs(tempJobs);
      if (jobId === activeJobId) {
        setActiveJobId(undefined);
      }
      onDelete?.(tempJobs, jobId, jobId === activeJobId);
    },
    [activeJobId, onDelete, registeredJobs]
  );

  const handleJobCardEdit = useCallback(
    (j: JobInputItem) => {
      const tempJobs = [...registeredJobs];
      for (let i = 0; i < tempJobs.length; i++) {
        if (tempJobs[i].jobId === j.jobId) {
          tempJobs[i] = j;
          break;
        }
      }
      setRegisteredJobs(tempJobs);
      setActiveJobId(j.jobId);
      onEdit?.(tempJobs, j.jobId, true);
    },
    [onEdit, registeredJobs]
  );

  /**
   * Callback for JobCard clicked
   *
   * @param {number} jobId Job ID of clicked job
   */
  const handleJobCardClick = useCallback(
    (jobId: number) => {
      const clickedJob = registeredJobs.find((job) => job.jobId === jobId);
      if (clickedJob !== undefined) {
        setActiveJobId(jobId);
        onActiveJobChanged?.(clickedJob);
      }
    },
    [onActiveJobChanged, registeredJobs]
  );

  const handleAddClick = useCallback(() => {
    const tempJobs = [...registeredJobs];
    const maxJobId = Math.max(...tempJobs.map((job) => job.jobId));
    const newJob = createInitialJoInputItem(maxJobId + 1);
    tempJobs?.push(newJob);
    setActiveJobId(newJob.jobId);
    setRegisteredJobs(tempJobs);
    onAdd?.(tempJobs, newJob.jobId, true);
  }, [onAdd, registeredJobs]);

  return (
    <PanelContentsContainer>
      <Box sx={{ display: "flex", padding: "5px" }}>
        <Typography variant="h5">{t("AWVUI-MSG203")}</Typography>
      </Box>
      <Stack spacing={2}>
        {registeredJobs?.map((j) => {
          return (
            <JobInputCard
              key={`Job-Input-${j.jobId}`}
              job={j}
              active={j.jobId === activeJobId}
              deletable={registeredJobs.length > 1}
              onDelete={handleJobCardDelete}
              onEdit={handleJobCardEdit}
              onClick={handleJobCardClick}
            />
          );
        })}
      </Stack>
      <Box sx={{ padding: 4 }}></Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "right",
          position: "absolute",
          bottom: 64,
          right: 20,
        }}
      >
        <BorderedIconButton onClick={handleAddClick}>
          <AddIcon />
        </BorderedIconButton>
      </Box>
    </PanelContentsContainer>
  );
};
